import { useEffect, useMemo, useState } from "react";

import { haversine } from "../lib/util.js";

/* 마커는 24px 원이다. 중심이 이만큼 안에 들어오면 서로를 가려서, 겹친 채로는 원하는
   하나를 누를 수가 없다 — 한 건물에 제휴점이 넷씩 있는 동네라 실제로 그렇게 된다.
   그때부터 한 핀으로 접고, 그 핀이 안에 든 곳들의 목록을 연다. */
const HUDDLE_PX = 28;

/* 겹침은 화면에서 일어나는 일이라 판정도 픽셀로 해야 한다. 미터로 못박으면 확대해도
   영영 안 풀리거나(값이 크면) 축소해도 계속 겹친다(값이 작으면). 그래서 배율이 바뀔
   때마다 '1픽셀이 몇 미터인가'를 지도에 물어서 반경을 다시 만든다. */
function metersPerPixel(map) {
  const b = map.getBounds();
  const sw = b.getSouthWest();
  const ne = b.getNorthEast();
  const px = map.getNode().offsetWidth;
  return px ? haversine(sw.getLat(), sw.getLng(), sw.getLat(), ne.getLng()) / px : 0;
}

/** 겹쳐 보이는 것끼리 접는다. 앵커는 첫 멤버의 좌표다 — 평균을 내면 아무 가게도 없는
    자리를 가리키게 되고, 멤버가 하나 붙을 때마다 핀이 조금씩 움직인다. */
function clusterize(pts, radiusM) {
  const out = [];
  for (const s of pts) {
    const hit = out.find((c) => haversine(c.lat, c.lng, s.lat, s.lng) <= radiusM);
    if (hit) hit.members.push(s);
    else out.push({ id: s.id, lat: s.lat, lng: s.lng, members: [s] });
  }
  return out;
}

/**
 * 지도에 찍을 핀 목록. 한 핀 = 한 묶음이고, 멤버가 하나뿐인 묶음이 곧 평범한 마커다.
 * 부르는 쪽은 `members.length` 만 보고 마커를 고르면 된다.
 *
 * soloId 는 무리에서 꺼내둘 한 곳이다(선택된 가게). 말풍선 꼬리가 가리킬 핀이 그
 * 가게의 좌표에 서 있어야 하고, 색도 그 집 상태여야 한다. 무리 안에 둔 채 묶음 핀에
 * 선택 표시를 얹으면 꼬리는 최대 28px 어긋난 곳을 가리키고 상태색은 사라진다.
 */
export function useClusters(map, rows, soloId) {
  const [mPerPx, setMPerPx] = useState(0);

  useEffect(() => {
    if (!map) return;
    const kakao = window.kakao;
    const sync = () => {
      const v = metersPerPixel(map);
      /* 5% 안쪽 변화는 무시한다. idle 은 드래그를 멈출 때마다 울리고, 위도가 바뀌면
         같은 배율에서도 값이 미세하게 달라진다 — 그대로 받으면 지도를 조금 미는 것만
         으로 묶음이 다시 짜여서 핀이 눈앞에서 재배치된다. 같은 값을 돌려주면 React 가
         상태 갱신을 접는다. */
      if (v) setMPerPx((prev) => (prev && Math.abs(v - prev) / prev < 0.05 ? prev : v));
    };
    sync();
    /* zoom_changed 가 아니라 idle 이다. 확대는 애니메이션이라 zoom_changed 시점의
       범위는 아직 가는 중이고, 그걸로 재면 반경이 한 박자 틀린다. */
    kakao.maps.event.addListener(map, "idle", sync);
    return () => kakao.maps.event.removeListener(map, "idle", sync);
  }, [map]);

  return useMemo(() => {
    const pts = rows.filter((s) => s.lat != null);
    const solo = pts.find((s) => s.id === soloId) || null;
    // 배율을 아직 못 쟀으면(지도가 없거나 첫 프레임) 접지 않는다 — 예전 그대로 그린다.
    if (!mPerPx) return pts.map((s) => ({ id: s.id, lat: s.lat, lng: s.lng, members: [s] }));
    const out = clusterize(solo ? pts.filter((s) => s !== solo) : pts, HUDDLE_PX * mPerPx);
    if (solo) out.push({ id: solo.id, lat: solo.lat, lng: solo.lng, members: [solo] });
    return out;
  }, [rows, mPerPx, soloId]);
}
